import * as React from "react"
import { cn } from "@/lib/utils"

// One form field in the design system's Forms pattern (M7): the label, the
// control, helper text in text-xs muted and the error in text-xs danger in
// the same slot. The control is passed in and wired with `fieldAria`.

export function fieldAria(id: string, { error, hint }: { error?: string; hint?: React.ReactNode }) {
  const describedBy = [error ? `${id}-error` : null, hint ? `${id}-hint` : null].filter(Boolean).join(" ")
  return {
    id,
    "aria-invalid": error ? (true as const) : undefined,
    "aria-describedby": describedBy || undefined,
  }
}

export function Field({
  id,
  label,
  hint,
  error,
  children,
  className,
}: {
  id: string
  label: React.ReactNode
  hint?: React.ReactNode
  error?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && (
        <p id={`${id}-hint`} className="text-xs text-ink-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-xs text-danger-display">
          {error}
        </p>
      )}
    </div>
  )
}

/**
 * A checkbox with its label as body copy (the Forms section's checkbox row),
 * padded to a 44px touch target. `control` is the <Checkbox>.
 */
export function CheckboxRow({
  control,
  label,
  description,
  className,
}: {
  control: React.ReactNode
  label: React.ReactNode
  description?: React.ReactNode
  className?: string
}) {
  return (
    <label className={cn("flex min-h-11 cursor-pointer items-start gap-3 py-2.5 text-sm font-normal text-ink-body", className)}>
      <span className="mt-0.5 flex shrink-0">{control}</span>
      <span>
        <span className="font-medium text-ink-display">{label}</span>
        {description && <span className="block text-xs text-ink-muted">{description}</span>}
      </span>
    </label>
  )
}
